import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Megaphone, Target, Calendar, Wallet, Users, Check, Radio, Compass, FlaskConical } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useDemo } from "@/components/demo/DemoUi";
import {
  DEMO_CAMPAIGN_SCENARIO, DEMO_CAMPAIGN_UI, SIMULATION_BANNER, DEMO_CAMPAIGN_ANCHOR, loc,
} from "@/config/demoCampaignScenario";

const nf = new Intl.NumberFormat("fr-FR");
const fmtMoney = (v, cur) => `${nf.format(Math.round(v))} ${cur || ""}`.trim();

function SimulationBanner({ text }) {
  return (
    <div
      data-testid="demo-simulation-banner"
      role="note"
      className="flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <FlaskConical size={16} className="mt-0.5 shrink-0 text-amber-600" />
      <p><strong className="font-semibold">Simulation.</strong> {text}</p>
    </div>
  );
}

function Field({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid}>
      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        <Icon size={12} /> {title}
      </p>
      <div className="mt-1 text-sm text-[#17201A]">{children}</div>
    </div>
  );
}

// Read-only presentational card for the SINGLE simulated France -> Mauritius
// campaign. Visible ONLY in demo context. No create/edit/delete controls.
export default function DemoCampaignScenario() {
  const { isDemo } = useDemo();
  const { lang } = useLang();
  const location = useLocation();
  const ref = useRef(null);

  useEffect(() => {
    if (isDemo && ref.current && location.hash === `#${DEMO_CAMPAIGN_ANCHOR}`) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isDemo, location.hash]);

  if (!isDemo) return null;
  const t = DEMO_CAMPAIGN_UI[lang] || DEMO_CAMPAIGN_UI.fr;
  const s = DEMO_CAMPAIGN_SCENARIO;

  return (
    <section
      id={DEMO_CAMPAIGN_ANCHOR}
      ref={ref}
      data-testid="demo-campaign-scenario"
      className="scroll-mt-24 space-y-4 rounded-2xl border border-indigo-200 bg-indigo-50/50 p-5 shadow-sm"
      aria-labelledby="demo-campaign-title"
    >
      <SimulationBanner text={loc(SIMULATION_BANNER, lang)} />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3D2B] to-[#2C5540]">
            <Megaphone size={18} className="text-[#5C9873]" />
          </div>
          <div>
            <h2 id="demo-campaign-title" className="font-heading text-lg font-bold tracking-tight text-[#17201A]" data-testid="demo-campaign-name">
              {loc(s.name, lang)}
            </h2>
            <span
              data-testid="demo-campaign-status"
              className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#F3EFE3] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#7A6A3C]"
            >
              <Check size={12} /> {t.statusLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Signal -> Objective narrative */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-rose-200 bg-white p-4" data-testid="demo-campaign-signal">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-rose-500">
            <Radio size={12} /> {t.signalTitle}
          </p>
          <p className="mt-1 text-sm text-[#475569]">{loc(s.signal, lang)}</p>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-white p-4" data-testid="demo-campaign-objective">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-600">
            <Target size={12} /> {t.objectiveTitle}
          </p>
          <p className="mt-1 text-sm text-[#475569]">{loc(s.objective, lang)}</p>
        </div>
      </div>

      {/* Audience / Period / Budget */}
      <div className="grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-3">
        <Field icon={Users} title={t.audienceTitle} testid="demo-campaign-audience">{loc(s.audience, lang)}</Field>
        <Field icon={Calendar} title={t.periodTitle} testid="demo-campaign-period">{loc(s.period, lang)}</Field>
        <Field icon={Wallet} title={t.budgetTitle} testid="demo-campaign-budget">
          <span className="font-bold">{fmtMoney(s.budget, s.currency)}</span>
        </Field>
      </div>

      {/* Channels */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="demo-campaign-channels">
        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          <Compass size={12} /> {t.channelsTitle}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {s.channels.map((c) => (
            <span key={c.code} data-testid={`demo-campaign-channel-${c.code}`}
              className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
              {loc(c.label, lang)}
            </span>
          ))}
        </div>
      </div>

      {/* Partners */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="demo-campaign-partners">
        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          <Users size={12} /> {t.partnersTitle}
        </p>
        <ul className="mt-2 space-y-1.5">
          {s.partners.map((p) => (
            <li key={p.name} data-testid={`demo-campaign-partner-${p.name}`} className="flex items-center gap-2 text-sm text-[#17201A]">
              <span className="font-semibold">{p.name}</span>
              <span className="text-xs text-slate-400">· {loc(p.role, lang)}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
